/**
 * paymentService.ts
 *
 * Централізована логіка виконання платежів.
 *
 * Вибір ендпоінту:
 *   psp === 'balance' AND playerData.balance >= price → createEndpoint
 *   всі інші випадки                                 → reserveEndpoint
 *
 * Zimpler/PIX — повертає type: 'zimpler'
 *   URL для редіректу будується в контейнері де є доступ до ticket id (betcard code)
 */

import axiosAuth from './axiosAuth';

const IFRAME_PSPS = new Set(['brite', 'paystack']);

export type PaymentResultType = 'tracking' | 'brite' | 'paystack' | 'zimpler';

export interface PaymentResult {
    type:   PaymentResultType;
    code:   string;
    token?: string;
}

export interface PlayerData {
    id?:            string | number;
    phone?:         string;
    email?:         string | null;
    currency:       string;
    balance:        number;
    lang?:          string;
    feature_flags?: Record<string, any>;
}

export const executePayment = async (
    psp:                string,
    ticketPayload:      Record<string, any>,
    reserveEndpoint:    string,
    createEndpoint:     string,
    playerData:         PlayerData,
    payloadTransformer?: (payload: Record<string, any>) => Record<string, any>,
): Promise<PaymentResult> => {

    const { psp: _psp, player_id: _player_id, msisdn: _msisdn, ...cleanPayload } = ticketPayload;

    const transformedPayload = payloadTransformer
        ? payloadTransformer(cleanPayload)
        : cleanPayload;

    const useCreate = psp === 'balance'
        && playerData.balance >= (transformedPayload.price ?? 0);

    const endpoint = useCreate ? createEndpoint : reserveEndpoint;

    const requestBody = psp === 'brite'
        ? { ...transformedPayload, psp: 'brite', player_id: 'brite' }
        : { ...transformedPayload, psp };

    const response = await axiosAuth.post<{ code: string; token?: string }>(
        endpoint,
        requestBody,
    );

    const { code, token } = response.data;

    if (IFRAME_PSPS.has(psp) && token) {
        return { type: psp as PaymentResultType, code, token };
    }

    // Zimpler/PIX — повертаємо type: 'zimpler'
    // URL будується в контейнері де є ticket id і price
    if (psp === 'zimpler') {
        return { type: 'zimpler', code };
    }

    return { type: 'tracking', code };
};

// ── Payload transformers ──────────────────────────────────────────────────────

export const quicktipTransformer = undefined;

export const superbetTransformer = (payload: Record<string, any>): Record<string, any> => ({
    ...payload,
    bets: Array.isArray(payload.bets)
        ? payload.bets.map((bet: any) => typeof bet === 'object' ? bet.betID : bet)
        : payload.bets,
});